import Steps from "common/Steps/Steps";
import { SinglePageLayoutContext } from "layout/SinglePageLayout";
import {
  useContext,
  createContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import labels from "utils/LocalizedStrings";
import sanitize from "utils/SanitizeHtml";
import ProvideInformation from "./ProvideInformation";
import ReviewInformation from "./ReviewInformation";
import SubmitInformation from "./SubmitInformation";
import Button from "common/Button/Button";
import cloneDeep from "lodash/cloneDeep";
import debounce from "lodash/debounce";
import {
  useElectionMutation,
  useGetStaticDataQuery,
  useLazyGetInvestmentDetailsQuery,
  useLazyGetParticipantInfoQuery,
  useMaritalConsentMutation,
  usePaymentRatesQuery,
} from "service/APIService";
import {
  checkAgeDifference,
  getAge,
  getCountryName,
  getCurrentDay,
  getPDFFooter,
  getPDFHeader,
  getPDFStylesheet,
  getRequiredIds,
  percentageFormatterForDisplay,
  scrollToTop,
} from "utils/Utilities";
import { validate } from "./validationSchema";
import Spinner from "common/Spinner/Spinner";
import ErrorMessage from "common/ErrorMessage/ErrorMessage";
import { setIncomeElectionInStore } from "store/incomeElectionSlice";
import {
  PDFDownloadLink,
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
} from "@react-pdf/renderer";
import { jointLifeInfo } from "components/Common/JointLifeInformation";
import { personalInfo } from "components/Common/PersonalInformation";
import { setParticipantDetails } from "store/authSlice";
import { setInvestmentDetailsInStore } from "store/commonSlice";

const styles = getPDFStylesheet(StyleSheet);

const IncomeElection = () => {
  const singlePageLayoutContext = useContext(SinglePageLayoutContext);
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const participantDetails = useSelector(
    (state) => state?.authReducer?.participantDetails
  );
  const incomeElectionInStore = useSelector(
    (state) => state?.incomeElectionReducer?.incomeElectionInStore
  );

  const investmentDetails = useSelector(
    (state) => state?.commonReducer?.investmentDetailsInStore
  );

  const [error, setError] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [nextDisabled, setNextDisabled] = useState(false);
  const [payload, setPayload] = useState({});

  const { data: paymentRates } = usePaymentRatesQuery({
    ...getRequiredIds(participantDetails),
  });
  const { data: staticData, isFetching } = useGetStaticDataQuery();
  const [maritalConsentApi] = useMaritalConsentMutation();
  const [electionApi, { isLoading: isLoading1 }] = useElectionMutation();
  const [participantMutation, { isLoading: isLoading2 }] =
    useLazyGetParticipantInfoQuery();
  const [investmentDetailsMutation, { isLoading: isLoading3 }] =
    useLazyGetInvestmentDetailsQuery();

  const debounceState = useCallback(
    debounce((value, state, func) => {
      func(value, state);
    }, 300),
    []
  );

  const checkUSStateSelected = (value) => {
    return staticData?.usStateList?.find(
      (e) => e.governmentUnitId == value || payload?.annuitantState
    );
  };

  const updatePayload = (value, key) => {
    const updated = cloneDeep(payload);

    if (key === "annuitantState") {
      updated[key] = value;
      updated["usResident"] = checkUSStateSelected(value) ? "Y" : "N";
      updated["annuitantUsResidencyCode"] = checkUSStateSelected(value)
        ? "C"
        : "N";
      updated["annuitantAddressTypeCode"] = checkUSStateSelected(value)
        ? "D"
        : "F";
      updated["countryName"] = getCountryName(value, staticData);
      updated["annuitantStateName"] = getStateInfo(value)?.name;
    } else {
      updated[key] = value;
    }

    setPayload({ ...updated });
  };

  const onChange = async (value, state) => {
    debounceState(value, state, updatePayload);
    setErrors({ ...errors, [state]: await validate(value, [state]) });
  };

  const errorProps = (state) => {
    return {
      value: payload[state] ?? "",
      error: errors[state],
      touched: touched[state],
      touch: () => setTouched({ ...touched, [state]: true }),
    };
  };

  const addressSameAsOwner = () => {
    const stateValue = (
      participantDetails.usResident === "Y"
        ? getUsStateData()
        : getCountryData()
    ).filter((e) => e.shortCode === participantDetails.pptStateCountryCode)[0];
    setPayload({
      ...payload,
      annuitantAddressLine1: participantDetails.address1,
      annuitantAddressLine2: participantDetails.address2,
      annuitantAddressLine3: participantDetails.address3,
      annuitantCity: participantDetails.city,
      annuitantState: stateValue?.governmentUnitId,
      annuitantStateName: stateValue?.governmentUnitName,
      annuitantZipCode: participantDetails.zipCode,
      annuitantUsResidencyCode:
        participantDetails.usResident === "Y" ? "C" : "N",
      annuitantAddressTypeCode:
        participantDetails.usResident === "Y" ? "D" : "F",
      countryName: participantDetails.pptStateCountryValue,
    });
  };

  const getStateCountryData = () => {
    if (staticData) {
      const dropdownData = [];
      getCountryData().forEach((e) => {
        dropdownData.push({
          value: e.governmentUnitId,
          label: " - " + e.governmentUnitName,
          shortCode: e.shortCode,
          name: e.governmentUnitName,
        });
      });

      getUsStateData().forEach((e) => {
        dropdownData.push({
          value: e.governmentUnitId,
          label: e.shortCode + " - " + e.governmentUnitName,
          shortCode: e.shortCode,
          name: e.governmentUnitName,
        });
      });

      return dropdownData;
    } else {
      return [];
    }
  };

  const getUsStateData = () => {
    return cloneDeep(staticData?.usStateList);
  };

  const getCountryData = () => {
    let countryList = cloneDeep(staticData?.countryList);
    countryList.shift();
    return countryList;
  };

  const getStateInfo = (value) => {
    return getStateCountryData().filter((e) => e.value == value)[0];
  };

  const checkDisabled = () => {
    let flag = false;
    [
      "annuitantTaxId",
      "annuitantBirthDate",
      "annuitantFirstName",
      "annuitantLastName",
      "annuitantAddressLine1",
      "annuitantCity",
      "annuitantState",
      "annuitantEmailAddress",
      "annuitantPhoneNumber",
      "annuitantGenderCode",
    ].forEach((e) => {
      if (!payload[e]) {
        flag = true;
      }
    });
    setNextDisabled(flag);
  };

  const cancel = () => {
    if (payload.maritalStatusConfirm === "N") {
      navigate("/dashboard");
    } else {
      navigate(-1);
    }
  };

  const navigateToStep2 = () => {
    setErrors({});
    setError(null);
    dispatch(setIncomeElectionInStore(cloneDeep(payload)));
    if (payload.maritalStatusConfirm === "N") {
      callMaritalConsent().then(() => {
        navigate("/income-election/2");
        participantMutation({ ...getRequiredIds(participantDetails) })
          .unwrap()
          .then((response) => {
            dispatch(setParticipantDetails(response));
          });
      });
    } else {
      navigate("/income-election/2");
      scrollToTop();
    }
  };

  const navigateToStep3 = async () => {
    if (payload.electionChoice === "J") {
      if (
        checkAgeDifference(
          participantDetails.dob,
          "MM/DD/YYYY",
          payload.annuitantBirthDate,
          "YYYY-MM-DD",
          10
        )
      ) {
        setError({
          errorCode: "",
          errorMessage: labels?.incomeElection?.spouseAgeError,
        });
        return;
      }
    }
    try {
      await finalSubmit(true);
      setError(null);
      dispatch(setIncomeElectionInStore(cloneDeep(payload)));
      navigate("/income-election/3");
    } catch (err) {}
  };

  const finalSubmit = async (validate) => {
    scrollToTop();
    setError(null);

    let body = {
      ...getRequiredIds(participantDetails),
      validateOnly: validate,
    };
    if (payload.electionChoice === "S") {
      body.electionChoice = "S";
    } else {
      const stateVal = getStateInfo(payload.annuitantState);
      body = {
        ...body,
        ...payload,
        annuitantAddressLine4: "",
        annuitantAddressLine5: "",
        annuitantMaritalStatusCode: "",
        annuitantPhoneNumberExt: "",
        annuitantPrefix: "",
        annuitantSuffix: "",
        civilUnionCode: payload.civilUnionCode ? "Y" : "N",
        annuitantState: stateVal?.shortCode,
        annuitantStateName: stateVal?.name,
      };
    }
    return new Promise((resolve, reject) => {
      electionApi(body)
        .unwrap()
        .then(() => {
          if (validate === "false") {
            callMaritalConsent().then(() => {
              participantMutation({ ...getRequiredIds(participantDetails) })
                .unwrap()
                .then((response) => {
                  dispatch(setParticipantDetails(response));
                  investmentDetailsMutation({
                    ...getRequiredIds(participantDetails),
                  })
                    .unwrap()
                    .then((response) => {
                      dispatch(setInvestmentDetailsInStore(response));
                      navigate("/income-election/4");
                    });
                });
            });
          } else {
            resolve(true);
          }
        })
        .catch((err) => {
          setError(err?.data?.messages);
          reject(false);
        });
    });
  };

  const callMaritalConsent = () => {
    return new Promise((resolve, reject) => {
      maritalConsentApi({
        ...getRequiredIds(participantDetails),
        ownerMaritalConsentCd: payload.maritalStatusConfirm,
        productOwnerElectionConsentCd:
          payload.maritalStatusConfirm === "Y" ? "Y" : undefined,
        productOwnerElectionStatusCd:
          payload.maritalStatusConfirm === "Y" ? "Y" : undefined,
        productOwnerElectionEffectiveDate: getCurrentDay(),
        mobile:
          participantDetails?.mobile === payload?.mobile
            ? null
            : payload?.mobile || "",
        email:
          participantDetails?.email === payload?.email
            ? null
            : payload?.email || "",
      })
        .unwrap()
        .then((res) => {
          if (res?.status === "Success") {
            resolve();
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  };

  const getPayoutRate = (type, age) => {
    if (type === "single") {
      return percentageFormatterForDisplay(
        paymentRates?.paymentRates?.find(
          (e) => e.age == getAge(participantDetails.dob)
        )?.paymentRateforSingleElection
      );
    } else {
      return percentageFormatterForDisplay(
        paymentRates?.paymentRates?.find(
          (e) => e.age == (age || getAge(participantDetails.dob))
        )?.paymentRateforJointElection
      );
    }
  };

  const MyPdfDocument = () => (
    <Document>
      <Page style={styles.page}>
        {getPDFHeader(View, Image, styles, Text, participantDetails)}

        <Text style={styles.sectionTitle}>
          {labels?.incomeElection?.incomeBenefitElection}
          {payload.electionChoice === "J"
            ? labels?.incomeElection?.joint
            : labels?.incomeElection?.single}
        </Text>

        <Text style={styles.sectionTitle}>
          {labels?.incomeElection?.personalInformation}
        </Text>
        {personalInfo({ ...payload, incomeElectionPage: true }).map(
          (e, index) => {
            return (
              !e.hide && (
                <View style={styles.row} key={e.label + index}>
                  <Text>{labels?.incomeElection[e.label]}</Text>
                  <Text style={styles.label}>{e.value || "-"}</Text>
                </View>
              )
            );
          }
        )}
        <View style={styles.row}>
          <Text>{labels?.incomeElection?.payoutRate}</Text>
          <Text style={styles.label}>
            {getPayoutRate(payload.electionChoice === "S" ? "single" : "joint")}
          </Text>
        </View>
        {payload.electionChoice === "J" && (
          <>
            <Text style={styles.sectionTitle}>
              {labels?.incomeElection?.jointLifeInformation}
            </Text>
            {jointLifeInfo(payload, false).map((e, index) => {
              return (
                !e.hide && (
                  <View style={styles.row} key={e.label + index}>
                    <Text>{labels?.incomeElection[e.label]}</Text>
                    <Text style={styles.label}>{e.value}</Text>
                  </View>
                )
              );
            })}
          </>
        )}

        {getPDFFooter(View, styles, Text)}
      </Page>
    </Document>
  );

  const IncomeElectionContext = createContext({
    labels,
    navigate,
    sanitize,
    payload,
    setPayload,
    updatePayload,
    errors,
    setErrors,
    setError,
    participantDetails,
    nextDisabled,
    setNextDisabled,
    paymentRates,
    cancel: cancel,
    navigateToStep2: navigateToStep2,
    navigateToStep3: navigateToStep3,
    participantAge: getAge(participantDetails.dob),
    onChange,
    errorProps,
    addressSameAsOwner,
    staticData,
    finalSubmit,
    cloneDeep,
    checkUSStateSelected,
    getStateCountryData,
    getPayoutRate,
  });

  useEffect(() => {
    if (payload.electionChoice === "J") {
      checkDisabled();
    } else {
      setNextDisabled(false);
    }
  }, [payload]);

  useEffect(() => {
    singlePageLayoutContext({
      title: `${labels?.incomeElection?.title}${
        id == 4 ? labels?.incomeElection?.confirmation : ""
      }`,
      buttonText: id == 4 ? "Close" : labels?.incomeElection?.cancel,
      buttonPath: "/dashboard",
      hideCancelModal: id == 4,
    });
    setErrors({});
    scrollToTop();
    setError(null);
  }, [id]);

  useEffect(() => {
    if (incomeElectionInStore?.electionChoice) {
      setPayload(cloneDeep(incomeElectionInStore));
    } else {
      setPayload({
        maritalStatusConfirm: "Y",
        electionChoice:
          ["S", "D", "W"].indexOf(participantDetails.maritalStatusCode) > -1
            ? "S"
            : "J",
        ...cloneDeep(participantDetails),
      });
    }
    return () => {
      debounceState.cancel();
    };
  }, []);

  if ((participantDetails.phaseCode !== "A") & (id != "4")) {
    return <></>;
  }
  if (id == 1) {
    return (
      <section className="single-page-container">
        {isFetching && <Spinner />}
        <Steps
          title={labels?.incomeElection?.reviewInfo}
          current="1"
          total="3"
          description={sanitize(
            investmentDetails?.fundLevelBalance?.some((e) => e.fundType === "T")
              ? labels?.incomeElection?.guaranteedMsg
              : labels?.incomeElection?.guaranteedMsgForBalancedFund,
          )}
          wrapperClassNames="pb-0"
        />
        <ReviewInformation context={IncomeElectionContext} />
      </section>
    );
  } else if (id == 2) {
    return (
      <section className="single-page-container">
        {isLoading1 && <Spinner />}
        {error ? <ErrorMessage wrapperClassName="mb-3" error={error} /> : ""}
        {payload?.maritalStatusConfirm === "Y" && (
          <Steps
            title={labels?.incomeElection?.makeYourIncomeElection}
            current="2"
            total="3"
            description={sanitize(
              labels?.incomeElection?.makeYourIncomeElectionMsg
            )}
            wrapperClassNames="pb-0"
          />
        )}
        <ProvideInformation context={IncomeElectionContext} />
      </section>
    );
  } else if (id == 3) {
    return (
      <section className="single-page-container">
        {(isLoading1 || isLoading2 || isLoading3) && <Spinner />}
        {error ? <ErrorMessage wrapperClassName="mb-3" error={error} /> : ""}
        <Steps current="3" total="3" title="Confirm Your Election" />
        <SubmitInformation context={IncomeElectionContext} />
      </section>
    );
  } else if (id == 4) {
    return (
      <section className="single-page-container text-center">
        <div className="mb-3">{labels?.incomeElection?.submittedMsg}</div>
        <PDFDownloadLink
          document={<MyPdfDocument />}
          fileName="IncomeElection.pdf"
          className="d-flex justify-content-center"
        >
          <Button
            type="download"
            text={labels?.incomeElection?.download}
            wrapperClassName="col-md-2 py-2"
            ariaLabel="Download"
          />
        </PDFDownloadLink>
        <div
          className="mt-3"
          dangerouslySetInnerHTML={sanitize(
            labels?.incomeElection?.submittedMsg2
          )}
        />
        <p>
          {labels?.incomeElection?.readyToProceed}
          <Link to="/monthly-payout-election/1">
            {labels?.incomeElection?.clickHere}
          </Link>
        </p>
        <p>{labels?.incomeElection?.submittedMsg3}</p>
      </section>
    );
  }
};

export default IncomeElection;
